import { v4 as uuidv4 } from "uuid";
import { updateData } from "./firestoreService";
import { GridCellModes } from "@mui/x-data-grid";
import { copyToClipboardUtility } from "./documentUtils";

// Utility to copy table data to clipboard in export format.
export const handleCopyToClipboard = ({
  section,
  combinedData,
  setCopiedIcon,
  generateSectionExport
}) => {
  const copyText = generateSectionExport(section, {
    [section.dataField]: combinedData,
  });
  copyToClipboardUtility(Object.values(copyText)[0].data, setCopiedIcon, true);
};

// Utility to ensure all row IDs are unique with section prefix
export const ensureUniqueRowIds = (data, sectionDataField) => {
  return data.map(row => {
    // If the row ID doesn't already start with the section prefix, add it
    if (row.id && !row.id.startsWith(`${sectionDataField}-`)) {
      return { ...row, id: `${sectionDataField}-${row.id}` };
    }
    // If no ID exists, generate one with prefix
    if (!row.id) {
      return { ...row, id: `${sectionDataField}-${uuidv4()}` };
    }
    return row;
  });
};

// Save table data to Firestore
export const saveTableData = async (updatedData, section, patientId, docId) => {
  try {
    // Prevent duplicate empty entries - keep only one empty entry per section
    const entriesWithData = updatedData.filter(item => {
      return Object.keys(item).some(key => {
        if (['id', 'humanReview', 'isHistory', 'isNew', 'humanAdded', 'isDeleted', 'originalName', 'modifiedAt', 'processingStatus'].includes(key)) return false;
        const value = item[key];
        return value && value !== '' && value !== ' ' && (typeof value !== 'string' || value.trim() !== '');
      });
    });
    
    const emptyEntries = updatedData.filter(item => {
      return !Object.keys(item).some(key => {
        if (['id', 'humanReview', 'isHistory', 'isNew', 'humanAdded', 'isDeleted', 'originalName', 'modifiedAt', 'processingStatus'].includes(key)) return false;
        const value = item[key];
        return value && value !== '' && value !== ' ' && (typeof value !== 'string' || value.trim() !== '');
      });
    });

    // For data style: only keep one empty entry if no data entries exist
    // For table style: only keep one empty entry if no data entries exist
    const filteredData = [
      ...entriesWithData,
      ...(entriesWithData.length === 0 && emptyEntries.length > 0 ? [emptyEntries[0]] : [])
    ];

    const editedDataToSave = filteredData
      .map(item => {
        const { originalIndex, originalId, displayIndex, ...cleanItem } = item;
        // Remove section prefix from ID when saving to Firestore
        if (cleanItem.id && cleanItem.id.startsWith(`${section.dataField}-`)) {
          cleanItem.id = cleanItem.id.replace(`${section.dataField}-`, '');
        }
        return cleanItem;
      });

    const updatedMainData = { [section.dataField]: editedDataToSave };
    await updateData(`patients/${patientId}/documents`, docId, updatedMainData);
  } catch (error) {
    console.error("Error saving data:", error);
  }
};

// Add a new row to table data
export const addTableRow = (section, combinedData) => {
  const displayFields = Object.keys(section.fields);
  
  const newRow = displayFields.reduce(
    (acc, key) => {
      if (section.fields[key] && section.fields[key].type === "check") {
        acc[key] = true;
      } else {
        acc[key] = "";
      }
      return acc;
    },
    {
      isNew: true,
      humanReview: true,
      humanAdded: true,
      id: `${section.dataField}-${uuidv4()}`,
    }
  );
  
  return [newRow, ...combinedData];
};

// Toggle delete status of a row
export const toggleRowDelete = (combinedData, index) => {
  return combinedData.map((item, i) => {
    if (i === index) {
      const currentDeletedStatus = item.isDeleted || false;
      
      // Simple toggle - if it's being modified, it's no longer history
      const updatedItem = { 
        ...item, 
        isDeleted: !currentDeletedStatus,
        isHistory: false, // Any modification removes history status
        humanReview: true // Mark for review when modified
      };
      
      return updatedItem;
    }
    return item;
  });
};

// Handle adding a new row and saving to Firestore
export const handleAddRow = async (section, combinedData, setCombinedData, patientId, docId) => {
  const updatedData = addTableRow(section, combinedData);
  setCombinedData(updatedData);
  await saveTableData(updatedData, section, patientId, docId);
  return updatedData; // Return for auto-focus
};

// Handle tab navigation between cells
export const handleTabNavigation = (params, event, section, displayRows, setCellModesModel) => {
  if (event.key !== "Tab") return false;
  
  event.preventDefault();

  // Filter out non-editable fields (checkboxes and primary field in data style)
  const fields = Object.keys(section.fields).filter((field) => {
    const fieldConfig = section.fields[field];
    const isPrimaryField = field === section.primaryField;
    const isDataStyle = section.editorStyle === "data";
    
    return fieldConfig.type !== "check" && !(isPrimaryField && isDataStyle);
  });
  
  const currentFieldIndex = fields.indexOf(params.field);
  const currentRowIndex = displayRows.findIndex((row) => row.id === params.id);

  let nextField, nextRowId;

  if (event.shiftKey) {
    // Shift+Tab: Move to previous field/row
    if (currentFieldIndex > 0) {
      nextField = fields[currentFieldIndex - 1];
      nextRowId = params.id;
    } else if (currentRowIndex > 0) {
      nextField = fields[fields.length - 1];
      nextRowId = displayRows[currentRowIndex - 1].id;
    }
  } else {
    // Tab: Move to next field/row
    if (currentFieldIndex < fields.length - 1) {
      nextField = fields[currentFieldIndex + 1];
      nextRowId = params.id;
    } else if (currentRowIndex < displayRows.length - 1) {
      nextField = fields[0];
      nextRowId = displayRows[currentRowIndex + 1].id;
    }
  }

  if (nextField && nextRowId) {
    // First, explicitly exit current edit mode
    setCellModesModel((prevModel) => ({
      ...prevModel,
      [params.id]: {
        ...prevModel[params.id],
        [params.field]: { mode: GridCellModes.View }
      }
    }));

    // Then set all cells to view mode and enter edit mode for next cell
    setTimeout(() => {
      setCellModesModel((prevModel) => ({
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id] || {}).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
          }),
          {}
        ),
        [nextRowId]: {
          [nextField]: { mode: GridCellModes.Edit },
        },
      }));
    }, 10);
  }

  return true;
};
